import React, { useMemo, memo } from 'react';
import { CheckPicker, PickerHandle } from 'rsuite';
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree';
import styled, { createGlobalStyle } from 'styled-components';
import Icons from '@/common/view/icons';
import { BaseSelectProps, selectStyles, SelectStyleProps } from '@/common/view/form/Select';

const OverlayIfDisabled = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 100%;
  max-width: 260px;
  z-index: 10;
`;

const MenuList = styled.li`
  ${({ theme }) => theme.typescales.caption1};
  color: ${({ theme }) => theme.colors.gray50};
  white-space: pre-wrap;
`;
const PickerBox = styled.div<{ $disabled: boolean; $width: string }>`
  position: relative;
  width: ${({ $width }) => $width};
  ${OverlayIfDisabled} {
    display: none;
  }
  &:hover {
    ${OverlayIfDisabled} {
      display: ${({ $disabled }) => ($disabled ? 'block' : 'none')};
    }
  }
`;

export const MultiSelectGlobalStyle = createGlobalStyle`
  :root {
    --rs-picker-count-bg: ${({ theme }) => theme.colors.blue50};
    --rs-checkbox-checked-bg: ${({ theme }) => theme.colors.blue50};
    --rs-input-focus-border: ${({ theme }) => theme.colors.grayBlack};
  }

  .rs-picker-popup.rs-picker-check-menu {
    width: 400px;
    .rs-picker-check-menu.rs-picker-check-menu-items {
      .rs-check-item {
        ${({ theme }) => theme.typescales.body4};
      }
    }
    .rs-input-group:focus-within,
    .rs-input-group:not(.rs-input-group-disabled).rs-input-group-focus,
    .rs-input-group:not(.rs-input-group-disabled):hover {
      outline: 2px solid ${({ theme }) => theme.colors.grayBlack};
      border-color: transparent;
    }

    .rs-picker-none {
      padding: 8px 12px;
      ${({ theme }) => theme.typescales.body4};
    }
  }
`;
const StyledCheckPicker = styled(CheckPicker)<SelectStyleProps>`
  ${selectStyles}
  width: 100%!important;
  .rs-picker-value-item {
    ${({ theme }) => theme.typescales.body4};
  }
  .rs-picker-toggle-label {
    &:after {
      display: none;
    }
  }
  .rs-picker-value-list {
    display: none;
  }
  .rs-picker-value-count {
    line-height: 18px;
  }
`;

export type MultiSelectHandle = PickerHandle;
export type Option = {
  label: string;
  value: string;
  groupBy?: string;
};

interface MultiSelectProps extends BaseSelectProps {
  label?: string;
  options: Option[];
  value?: string[];
  onChange?: (value: string[]) => void;
  renderMenuItem?: (label: React.ReactNode, item: ItemDataType) => React.ReactNode;
  renderExtraFooter?: () => React.ReactNode;
  selectRef?: React.Ref<MultiSelectHandle>;
}
const SELECT_ALL_VALUE = '__SELECT_ALL__';

const MultiSelect: React.FC<MultiSelectProps> = memo(
  ({
    size = 'small',
    label,
    options,
    value = [],
    onChange,
    width,
    searchable = false,
    placeholder,
    error = false,
    disabled = false,
    cleanable = true,
    messages,
    placement,
    renderMenuItem,
    renderExtraFooter,
    selectRef,
    virtualized = false,
  }) => {
    const allValues = useMemo(() => options.map((opt) => opt.value), [options]);
    const isAllSelected = useMemo(
      () => allValues.length > 0 && allValues.every((v) => value.includes(v)),
      [allValues, value],
    );

    const { allOptions, selectedOptions } = useMemo(() => {
      const selected = options.filter((opt) => value.includes(opt.value));
      const unselected = options.filter((opt) => !value.includes(opt.value));
      return {
        allOptions: [{ label: '전체', value: SELECT_ALL_VALUE }, ...selected, ...unselected],
        selectedOptions: selected,
      };
    }, [options, value]);

    const internalValue = useMemo(() => (isAllSelected ? [SELECT_ALL_VALUE, ...value] : value), [isAllSelected, value]);

    const handleChange = (newValue: string[]) => {
      if (!onChange) return;

      const hasSelectAll = newValue.includes(SELECT_ALL_VALUE);
      const selectAllToggled = hasSelectAll !== isAllSelected;

      if (selectAllToggled) {
        onChange(hasSelectAll ? allValues : []);
      } else {
        onChange(newValue.filter((v) => v !== SELECT_ALL_VALUE));
      }
    };

    return (
      <PickerBox $disabled={disabled} $width={width || 'auto'}>
        <StyledCheckPicker
          ref={selectRef}
          label={value.length > 0 && label ? <span style={{ marginRight: '4px' }}>{label}</span> : null}
          placement={placement}
          $size={size}
          style={{ width: width || 'auto' }}
          cleanable={cleanable}
          $error={error}
          disabled={disabled}
          searchable={searchable}
          placeholder={placeholder}
          caretAs={Icons.ArrowFillDown}
          data={allOptions}
          virtualized={virtualized}
          value={internalValue}
          locale={messages}
          onChange={(v) => handleChange((v || []) as string[])}
          renderValue={(values) => {
            const realCount = values.filter((v) => v !== SELECT_ALL_VALUE).length;
            if (realCount === 0) return null;
            return <span className="rs-picker-value-count">{realCount}</span>;
          }}
          renderMenuItem={renderMenuItem}
          renderExtraFooter={renderExtraFooter}
        />
        <OverlayIfDisabled>
          <ul className="list-group">
            {selectedOptions.map((opt) => (
              <MenuList key={opt.value} className="list-group-item">
                {opt.label}
              </MenuList>
            ))}
          </ul>
        </OverlayIfDisabled>
      </PickerBox>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';

export default MultiSelect;
